import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" />;

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "client" ? "/portal" : "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (acc) => { setEmail(acc.email); setPassword(acc.pwd); };

  return (
    <div className="min-h-screen flex">
      {/* Left form */}
      <div className="flex-1 flex flex-col p-8 lg:p-16 max-w-2xl">
        <div className="flex items-center gap-3 mb-16">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-white text-lg" style={{ background: "#72BE44" }}>PT</div>
          <div>
            <div className="font-bold tracking-tight text-[#0F2A4A]">PT — ECMP Platform</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Your Environmental Compliance Managing Partner</div>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full">
          <p className="text-xs uppercase tracking-[0.22em] text-[#72BE44] mb-3">Sign in to continue</p>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-[#0F2A4A] mb-3">
            Clearance.<br />Compliance.<br />Crystal clear.
          </h1>
          <p className="text-stone-500 mb-10 leading-relaxed">
            Your Environmental Compliance Managing Partner — operations cockpit for EC, CTE/CTO, CRZ and ECMP retainers.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/10 transition"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/10 transition"
                required
              />
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 text-white font-medium py-3 rounded-lg transition disabled:opacity-50 hover:opacity-90"
              style={{ background: "#0F2A4A" }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>Sign in <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>

          <div className="mt-6 p-3 bg-stone-50 border border-stone-200 rounded-lg text-[11px] text-stone-600 leading-relaxed" data-testid="consent-notice">
            By logging in you consent to <b>PT</b> storing your project compliance data for service delivery.
          </div>
        </div>
      </div>

      {/* Right image */}
      <div className="hidden lg:block flex-1 relative overflow-hidden bg-stone-900">
        <img
          src="https://images.unsplash.com/photo-1774646598595-fa600737ecec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdyZWVuJTIwdG9wb2dyYXBoaWMlMjBtYXAlMjB0ZXh0dXJlfGVufDB8fHx8MTc4MTE4MzQ2MXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/60 via-stone-900/40 to-stone-900/80" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-xs uppercase tracking-[0.22em] text-emerald-300 mb-3">Master ECMP Workflow</div>
          <div className="text-2xl font-semibold leading-snug" style={{ fontFamily: "Bricolage Grotesque" }}>
            Lead → Proposal → Work Order → Project Charter → Dynamic Checklist → Submission → Approval → Compliance.
          </div>
        </div>
      </div>
    </div>
  );
}
